/**
 * Authentication Type Definitions
 *
 * Defines core types for authentication state management, user data,
 * and route configuration for the LearnBahn application.
 */

import { UserRole } from '@/types/enums/UserRole';

/**
 * User data structure (for storage and UI)
 * Uses snake_case to match backend and auto-generated DB models
 */
export interface AuthUser {
  id: string;
  username: string;
  display_name: string;
  organization_code: string;
  organization_title: string;
  organization_id: string;
  email: string;
  role: UserRole;
  profile_image?: string; // From users.profile_image column
  organization: {
    id: string;
    code: string;
    title: string;
    is_superadmin_org: boolean;
    profile_image?: string; // From organizations.profile_image column
  };
}

/**
 * Logout input (no data required)
 */
export type LogoutInput = Record<string, never>;

/**
 * Login UI output (used for storage and UI)
 * Wraps AuthUser with token information
 */
export interface LoginUIOutput {
  user: AuthUser;
  token: {
    access_token: string;
    expires_in: number;
    token_type: string;
  };
}

/**
 * Authentication state tracking.
 */
export type AuthState = {
  user: AuthUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
};

/**
 * Available screens in the application.
 */
export enum ScreenType {
  LOGIN = 'LOGIN',
  DASHBOARD = 'DASHBOARD',
  COMING_SOON = 'COMING_SOON',
}

/**
 * Configuration for a single route/screen.
 */
export interface RouteConfig {
  /** Screen identifier */
  screen: ScreenType;
  /** Whether this screen requires authentication */
  requiresAuth: boolean;
  /** Whether this screen is accessible only to unauthenticated users */
  publicOnly: boolean;
}

/**
 * Session metadata stored alongside user data.
 */
export interface SessionMetadata {
  /** Timestamp when the user logged in */
  loginTimestamp: number;
  /** Timestamp of last user activity */
  lastActivity: number;
  /** Optional session expiry time (future enhancement) */
  expiresAt?: number;
}
